import { Box, Typography } from "@mui/material";
import type { ReactNode } from "react";

/**
 * Page header for every section in the dashboard (Sales, Marketing,
 * Admin, Documentation, Support, Dashboard, Server status, …).
 *
 * **CRM is the exception.** CRM pages use a different, denser layout
 * with its own `components/ui/page-header.tsx`. Don't import this one
 * into anything under `apps/web/src/crm/`.
 *
 * Two slot props for the right-hand area — `actions` (legacy) and
 * `right` (newer) — both render in the same spot. Pages can use
 * whichever name reads best in context; the rendered output is
 * identical.
 *
 * Title is the DM Sans display face at text-xl→2xl (the theme's `h1`
 * variant supplies the font; size + weight set here). Consistent across
 * every dashboard section so the page chrome reads as one designed
 * system.
 */
export function PageHeader({
  title,
  subtitle,
  actions,
  right,
  dense = false,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  right?: ReactNode;
  /**
   * Compact variant: drops the default 24px bottom margin so the header
   * sits flush above a tightly-grouped follow-on element (e.g. a
   * breadcrumb row), letting the parent container own the spacing. Used by
   * the Documentation drill-down pages so the breadcrumbs sit right under
   * the title. Default `false` → unchanged for every other section.
   */
  dense?: boolean;
}): JSX.Element {
  const trailing = right ?? actions;
  return (
    <Box
      component="header"
      sx={{
        mb: dense ? 0 : 3,
        display: "flex",
        // Stack on mobile so the title + action cluster don't cram into one
        // narrow row (mirrors the CRM page-header's responsive pattern).
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "flex-start", sm: "flex-end" },
        justifyContent: "space-between",
        gap: { xs: 1.5, sm: 3 },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="h1"
          component="h1"
          sx={{
            fontSize: { xs: "1.25rem", sm: "1.5rem" },
            fontWeight: 600,
            lineHeight: 1.2,
            letterSpacing: "-0.01em",
            color: "text.primary",
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            component="p"
            sx={{ mt: 0.5, maxWidth: "42rem", fontSize: 14, lineHeight: 1.4, color: "text.secondary" }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
      {trailing && <Box sx={{ flexShrink: 0 }}>{trailing}</Box>}
    </Box>
  );
}
